import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { COGNITIVE_LANES } from '../mock';
import { metricsAPI, projectAPI, reviewAPI } from '../services/api';
import { 
  TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Clock, 
  ClipboardCheck, AlertTriangle, Activity, ChevronRight, Zap,
  Target, ArrowUpRight, ArrowDownRight, Minus, Play, Flame,
  Calendar, Award, Image, Send, FileCheck, FileText, Package,
  MapPin, Clipboard, Users, Shield, Sparkles, Building2
} from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';
import { 
  DemoRoleSwitcher, 
  UNIQUE_IP_COLORS,
  CHIEF_OF_STAFF_COLORS,
  getPulseRoleData 
} from '../components/focus';
import {
  PulseCommandCenterHero,
  PulseStatCard,
  PulseWorkAdvancingFeed,
  SoulFoodDivisionHealth,
  CognitiveLanesOverview,
  ExecutiveReviewPipeline,
  COS_PULSE_COLORS
} from '../components/ChiefOfStaff/ChiefOfStaffPulse';

// ================================================
// ANIMATED MOMENTUM INDICATOR
// ================================================
const MomentumIndicator = ({ status, label }) => {
  const configs = {
    'Strong': { 
      color: '#10B981', 
      glow: 'rgba(16, 185, 129, 0.5)', 
      pulseColor: 'rgba(16, 185, 129, 0.3)',
      icon: TrendingUp 
    },
    'Building': { 
      color: '#F59E0B', 
      glow: 'rgba(245, 158, 11, 0.5)', 
      pulseColor: 'rgba(245, 158, 11, 0.3)',
      icon: Zap 
    },
    'Steady': { 
      color: '#3B82F6', 
      glow: 'rgba(59, 130, 246, 0.5)', 
      pulseColor: 'rgba(59, 130, 246, 0.3)',
      icon: Activity 
    },
    'Focus Required': { 
      color: '#EF4444', 
      glow: 'rgba(239, 68, 68, 0.5)', 
      pulseColor: 'rgba(239, 68, 68, 0.3)',
      icon: AlertTriangle 
    }
  };
  
  const config = configs[status] || configs['Steady'];
  const Icon = config.icon;
  
  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        {/* Animated pulse rings */}
        <div 
          className="absolute inset-0 rounded-2xl animate-ping opacity-30"
          style={{ backgroundColor: config.pulseColor, animationDuration: '2s' }}
        />
        <div 
          className="relative w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ 
            backgroundColor: `${config.color}20`,
            boxShadow: `0 0 30px ${config.glow}`
          }}
        >
          <Icon className="w-8 h-8" style={{ color: config.color }} />
        </div>
      </div>
      <div>
        <div 
          className="text-4xl md:text-5xl font-black tracking-tight"
          style={{ 
            color: config.color,
            textShadow: `0 0 40px ${config.glow}`
          }}
        >
          {status}
        </div>
        <div 
          className="text-sm font-medium uppercase tracking-wider mt-1 px-3 py-1 rounded-full inline-block"
          style={{ backgroundColor: `${config.color}15`, color: config.color }}
        >
          {label}
        </div>
      </div>
    </div>
  );
};

// ================================================
// PERSONAL MOMENTUM HERO (Team Member & Ops Views)
// ================================================
const PersonalMomentumHero = ({ data, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  const statusConfig = {
    'on-track': { color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' },
    'building': { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' },
    'attention': { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)' }
  };
  const status = statusConfig[data.status] || statusConfig['on-track'];

  return (
    <div 
      className="relative rounded-3xl overflow-hidden"
      style={{
        background: isUniqueIP 
          ? 'linear-gradient(135deg, #451a03 0%, #78350f 40%, #451a03 100%)'
          : 'linear-gradient(135deg, #0D1B2A 0%, #1B263B 40%, #0D1B2A 100%)'
      }}
    >
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(ellipse at 20% 50%, ${accentColor}40 0%, transparent 50%),
                           radial-gradient(ellipse at 80% 80%, ${accentColor}20 0%, transparent 40%)`
        }}
      />
      
      <div className="relative p-8 md:p-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: status.bg }}
              >
                <CheckCircle2 className="w-7 h-7" style={{ color: status.color }} />
              </div>
              <div>
                <div 
                  className="text-3xl md:text-4xl font-black tracking-tight text-white"
                >
                  {data.headline}
                </div>
                <div 
                  className="text-lg font-medium mt-1"
                  style={{ color: status.color }}
                >
                  {data.subheadline}
                </div>
              </div>
            </div>
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Live • Updated just now
          </div>
        </div>
        
        <p className="text-base text-slate-300 max-w-3xl leading-relaxed">
          {data.supportingText}
        </p>
      </div>
    </div>
  );
};

// ================================================
// PERSONAL KPI CARD
// ================================================
const PersonalKPICard = ({ label, value, sublabel, target, progress, trend, trendLabel, status, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  const statusColors = {
    healthy: { color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)' },
    elevated: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
    high: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)' }
  };
  const statusConfig = status ? statusColors[status] : null;

  return (
    <div 
      className="rounded-2xl p-5 relative overflow-hidden"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
        {label}
      </p>
      <div className="flex items-end gap-2">
        <span 
          className="text-4xl font-black"
          style={{ color: statusConfig ? statusConfig.color : accentColor }}
        >
          {value}{typeof value === 'number' && target ? '' : status === 'healthy' || status === 'elevated' || status === 'high' ? '' : '%'}
        </span>
        {target && (
          <span className="text-lg font-medium mb-1" style={{ color: DESIGN.colors.textMuted }}>
            / {target}
          </span>
        )}
      </div>
      {sublabel && (
        <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>{sublabel}</p>
      )}
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-2">
          {trend > 0 ? (
            <ArrowUpRight className="w-4 h-4" style={{ color: '#10B981' }} />
          ) : trend < 0 ? (
            <ArrowDownRight className="w-4 h-4" style={{ color: '#EF4444' }} />
          ) : (
            <Minus className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
          )}
          <span 
            className="text-xs font-medium"
            style={{ color: trend > 0 ? '#10B981' : trend < 0 ? '#EF4444' : DESIGN.colors.textMuted }}
          >
            {trend > 0 ? '+' : ''}{trend}% {trendLabel}
          </span>
        </div>
      )}
      {statusConfig && (
        <div 
          className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-medium"
          style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}
        >
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusConfig.color }} />
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </div>
      )}
      {progress && (
        <div className="mt-3">
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
            <div 
              className="h-full rounded-full"
              style={{ width: `${progress}%`, backgroundColor: accentColor }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================
// PERSONAL WORK FEED
// ================================================
const PersonalWorkFeed = ({ items, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  const iconMap = {
    'CheckCircle2': CheckCircle2,
    'Image': Image,
    'Send': Send,
    'FileCheck': FileCheck,
    'FileText': FileText,
    'Package': Package,
    'Clipboard': Clipboard,
    'MapPin': MapPin
  };

  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: isUniqueIP ? UNIQUE_IP_COLORS.primarySubtle : DESIGN.colors.primarySubtle }}
        >
          <Activity className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            My Completed Work
          </h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Your recent achievements</p>
        </div>
      </div>
      
      <div className="space-y-3">
        {items.map((item) => {
          const Icon = iconMap[item.icon] || CheckCircle2;
          return (
            <div 
              key={item.id}
              className="flex items-start gap-3 p-3 rounded-xl"
              style={{ backgroundColor: DESIGN.colors.background }}
            >
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${accentColor}15` }}
              >
                <Icon className="w-4 h-4" style={{ color: accentColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                  {item.title}
                </p>
                <p className="text-xs mt-0.5" style={{ color: DESIGN.colors.textMuted }}>
                  {item.timestamp}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ================================================
// MY LANE PERFORMANCE (Single Lane View)
// ================================================
const MyLanePerformance = ({ data, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : data.laneColor || DESIGN.colors.primary;
  const vsAvgConfig = {
    above: { label: 'Above Average', color: '#10B981', icon: ArrowUpRight },
    'on-par': { label: 'On Par', color: '#3B82F6', icon: Minus },
    below: { label: 'Below Average', color: '#F59E0B', icon: ArrowDownRight }
  };
  const statusConfig = vsAvgConfig[data.myVsLaneAvg] || vsAvgConfig['on-par'];
  const StatusIcon = statusConfig.icon;

  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Target className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            {isUniqueIP ? 'My Operations Performance' : 'My Lane Performance'}
          </h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
            {data.lane} lane this week
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Lane Health</span>
          <span className="text-lg font-bold" style={{ color: accentColor }}>{data.laneHealth}%</span>
        </div>
        
        <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
          <div 
            className="h-full rounded-full"
            style={{ width: `${data.laneHealth}%`, backgroundColor: accentColor }}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 pt-3">
          <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
            <p className="text-2xl font-bold" style={{ color: accentColor }}>{data.myContribution}</p>
            <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>My Contribution</p>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
            <div className="flex items-center justify-center gap-1">
              <StatusIcon className="w-4 h-4" style={{ color: statusConfig.color }} />
              <span className="text-sm font-bold" style={{ color: statusConfig.color }}>
                {statusConfig.label}
              </span>
            </div>
            <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>vs Lane Avg</p>
          </div>
        </div>

        <div 
          className="flex items-center gap-2 p-3 rounded-xl text-xs"
          style={{ backgroundColor: `${data.assignmentsStatus === 'ahead' ? '#10B981' : data.assignmentsStatus === 'behind' ? '#F59E0B' : '#3B82F6'}10` }}
        >
          <CheckCircle2 
            className="w-4 h-4" 
            style={{ color: data.assignmentsStatus === 'ahead' ? '#10B981' : data.assignmentsStatus === 'behind' ? '#F59E0B' : '#3B82F6' }} 
          />
          <span style={{ color: DESIGN.colors.textSecondary }}>
            You're {data.assignmentsStatus === 'ahead' ? 'ahead' : data.assignmentsStatus === 'behind' ? 'behind' : 'on track'} on your lane assignments
          </span>
        </div>
      </div>
    </div>
  );
};

// ================================================
// TODAY'S PRIORITIES
// ================================================
const TodaysPriorities = ({ items, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  const priorityColors = {
    critical: '#EF4444',
    high: '#F59E0B',
    medium: '#3B82F6',
    low: '#10B981'
  };
  const statusColors = {
    'in-progress': '#F59E0B',
    'not-started': '#94A3B8',
    'upcoming': '#3B82F6',
    'planned': '#8B5CF6'
  };

  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
        >
          <Flame className="w-5 h-5" style={{ color: '#EF4444' }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            Today's Priorities
          </h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Your most urgent items</p>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((item, index) => (
          <div 
            key={item.id}
            className="flex items-center gap-3 p-3 rounded-xl"
            style={{ backgroundColor: DESIGN.colors.background }}
          >
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
              style={{ backgroundColor: `${priorityColors[item.priority]}15`, color: priorityColors[item.priority] }}
            >
              {index + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: DESIGN.colors.textPrimary }}>
                {item.title}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <Clock className="w-3 h-3" style={{ color: DESIGN.colors.textMuted }} />
                <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                  {item.dueTime || item.time}
                </span>
                <span 
                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ 
                    backgroundColor: `${statusColors[item.status]}15`,
                    color: statusColors[item.status]
                  }}
                >
                  {item.status.replace('-', ' ')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// PERSONAL WINS / SHIFT LOG
// ================================================
const PersonalWins = ({ items, title, isUniqueIP, isShiftLog }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;

  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Award className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            {title || 'Personal Wins'}
          </h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
            {isShiftLog ? 'Recent operational shifts' : 'Recent completions'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <div 
            key={item.id}
            className="flex items-center justify-between p-3 rounded-xl"
            style={{ backgroundColor: DESIGN.colors.background }}
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-4 h-4" style={{ color: accentColor }} />
              <span className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                {item.title || item.eventName}
              </span>
            </div>
            <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
              {item.completedAt || item.date}
              {item.hours && ` • ${item.hours}h`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// MY UPCOMING DEADLINES
// ================================================
const MyUpcomingDeadlines = ({ items, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  const priorityColors = {
    high: '#EF4444',
    medium: '#F59E0B',
    low: '#10B981'
  };

  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Calendar className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            My Upcoming Deadlines
          </h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Next 7 days</p>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <div 
            key={item.id}
            className="flex items-center justify-between p-3 rounded-xl"
            style={{ backgroundColor: DESIGN.colors.background }}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div 
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: priorityColors[item.priority] }}
              />
              <span className="text-sm font-medium truncate" style={{ color: DESIGN.colors.textPrimary }}>
                {item.title}
              </span>
            </div>
            <span 
              className="text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ml-2"
              style={{ 
                backgroundColor: item.dueDate === 'Today' ? 'rgba(239, 68, 68, 0.1)' : item.dueDate === 'Tomorrow' ? 'rgba(245, 158, 11, 0.1)' : DESIGN.colors.background,
                color: item.dueDate === 'Today' ? '#EF4444' : item.dueDate === 'Tomorrow' ? '#F59E0B' : DESIGN.colors.textMuted
              }}
            >
              {item.dueDate}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// UPCOMING EVENTS (For Unique IP Operations)
// ================================================
const UpcomingEvents = ({ events, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;

  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Calendar className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            Upcoming Events
          </h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Events you're supporting</p>
        </div>
      </div>

      <div className="space-y-3">
        {events.map((event) => (
          <div 
            key={event.id}
            className="p-4 rounded-xl"
            style={{ backgroundColor: DESIGN.colors.background }}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                {event.name}
              </h4>
              <span 
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
              >
                {event.date}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs" style={{ color: DESIGN.colors.textMuted }}>
              <MapPin className="w-3 h-3" />
              <span>{event.location}</span>
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <Users className="w-3 h-3" style={{ color: accentColor }} />
              <span style={{ color: accentColor }}>{event.myRole}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// PREMIUM STAT CARD
// ================================================
const PremiumStatCard = ({ label, value, change, changeLabel, status, icon: Icon }) => {
  const statusColors = {
    positive: { bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.02) 100%)', accent: '#10B981', border: 'rgba(16, 185, 129, 0.2)' },
    warning: { bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.02) 100%)', accent: '#F59E0B', border: 'rgba(245, 158, 11, 0.2)' },
    negative: { bg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.02) 100%)', accent: '#EF4444', border: 'rgba(239, 68, 68, 0.2)' },
    neutral: { bg: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.02) 100%)', accent: '#3B82F6', border: 'rgba(59, 130, 246, 0.2)' }
  };
  
  const config = statusColors[status] || statusColors.neutral;
  const isPositive = change > 0;
  
  return (
    <div 
      className="relative p-5 rounded-2xl overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
      style={{ 
        background: config.bg,
        border: `1px solid ${config.border}`
      }}
    >
      {/* Decorative corner accent */}
      <div 
        className="absolute top-0 right-0 w-20 h-20 opacity-10"
        style={{
          background: `radial-gradient(circle at top right, ${config.accent} 0%, transparent 70%)`
        }}
      />
      
      <div className="flex items-start justify-between mb-3">
        <span 
          className="text-xs font-bold uppercase tracking-wider"
          style={{ color: 'rgba(148, 200, 190, 0.9)' }}
        >
          {label}
        </span>
        {Icon && (
          <div 
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${config.accent}15` }}
          >
            <Icon className="w-4 h-4" style={{ color: config.accent }} />
          </div>
        )}
      </div>
      
      <div className="flex items-end justify-between">
        <div>
          <div 
            className="text-4xl font-black tracking-tight"
            style={{ color: '#FFFFFF' }}
          >
            {value}
          </div>
          {change !== undefined && (
            <div 
              className="flex items-center gap-1 mt-2 text-sm font-semibold"
              style={{ color: isPositive ? '#10B981' : change < 0 ? '#EF4444' : 'rgba(200, 215, 210, 0.8)' }}
            >
              {isPositive ? <ArrowUpRight className="w-4 h-4" /> : change < 0 ? <ArrowDownRight className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
              <span>{isPositive ? '+' : ''}{change} {changeLabel}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Hover reveal extra context */}
      <div className="absolute bottom-0 left-0 right-0 h-0 group-hover:h-10 transition-all duration-300 overflow-hidden">
        <div 
          className="px-5 py-2 text-xs font-medium"
          style={{ backgroundColor: `${config.accent}10`, color: config.accent }}
        >
          Click to view details →
        </div>
      </div>
    </div>
  );
};

// ================================================
// LIVE ACTIVITY FEED ITEM
// ================================================
const ActivityFeedItem = ({ title, status, timestamp, type }) => {
  const typeConfigs = {
    completed: { 
      icon: CheckCircle2, 
      color: '#10B981', 
      bg: 'rgba(16, 185, 129, 0.1)',
      glow: '0 0 20px rgba(16, 185, 129, 0.3)'
    },
    'in-progress': { 
      icon: Play, 
      color: '#00C9A7', 
      bg: 'rgba(0, 201, 167, 0.1)',
      glow: '0 0 20px rgba(0, 201, 167, 0.3)',
      animated: true
    },
    attention: { 
      icon: AlertCircle, 
      color: '#F59E0B', 
      bg: 'rgba(245, 158, 11, 0.1)',
      glow: '0 0 20px rgba(245, 158, 11, 0.3)',
      pulse: true
    }
  };
  
  const config = typeConfigs[type] || typeConfigs.completed;
  const Icon = config.icon;
  
  return (
    <div 
      className="flex items-start gap-4 p-4 rounded-xl transition-all duration-200 hover:shadow-lg group cursor-pointer"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      <div className="relative">
        {config.pulse && (
          <div 
            className="absolute inset-0 rounded-xl animate-pulse"
            style={{ backgroundColor: config.bg }}
          />
        )}
        {config.animated && (
          <div 
            className="absolute inset-0 rounded-xl animate-ping opacity-30"
            style={{ backgroundColor: config.color, animationDuration: '2s' }}
          />
        )}
        <div 
          className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
          style={{ 
            backgroundColor: config.bg,
            boxShadow: config.glow
          }}
        >
          <Icon className="w-5 h-5" style={{ color: config.color }} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p 
          className="text-sm font-semibold truncate group-hover:text-clip"
          style={{ color: DESIGN.colors.textPrimary }}
        >
          {title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span 
            className="text-xs font-medium"
            style={{ color: config.color }}
          >
            {status}
          </span>
          <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>•</span>
          <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{timestamp}</span>
        </div>
      </div>
      <ChevronRight 
        className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ color: DESIGN.colors.textMuted }}
      />
    </div>
  );
};

// ================================================
// PREMIUM LANE PERFORMANCE BAR
// ================================================
const LanePerformanceBar = ({ lane, percentage, activeCount, color }) => {
  return (
    <div className="group cursor-pointer">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div 
            className="w-4 h-4 rounded-full transition-transform group-hover:scale-125"
            style={{ 
              backgroundColor: color,
              boxShadow: `0 0 12px ${color}60`
            }}
          />
          <span 
            className="text-sm font-semibold"
            style={{ color: DESIGN.colors.textPrimary }}
          >
            {lane}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span 
            className="text-xs px-2 py-1 rounded-full font-medium"
            style={{ backgroundColor: DESIGN.colors.background, color: DESIGN.colors.textMuted }}
          >
            {activeCount} active
          </span>
          <span 
            className="text-sm font-bold w-12 text-right"
            style={{ color: DESIGN.colors.textPrimary }}
          >
            {percentage}%
          </span>
        </div>
      </div>
      
      <div className="relative h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
        {/* Gradient fill with glow */}
        <div 
          className="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${color}CC 0%, ${color} 100%)`,
            boxShadow: `0 0 20px ${color}60`
          }}
        />
        {/* Animated shine effect */}
        <div 
          className="absolute top-0 left-0 h-full w-full overflow-hidden rounded-full"
          style={{ width: `${percentage}%` }}
        >
          <div 
            className="h-full w-20 absolute animate-shimmer"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              animation: 'shimmer 3s infinite'
            }}
          />
        </div>
      </div>
    </div>
  );
};

// ================================================
// EXECUTIVE REVIEW ALERT CARD
// ================================================
const ExecutiveReviewCard = ({ metrics }) => {
  const slaAtRisk = metrics?.sla_at_risk > 0;
  
  return (
    <div 
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)'
      }}
    >
      {/* Animated background pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 80%, ${DESIGN.colors.primary} 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, #8B5CF6 0%, transparent 50%)`
        }}
      />
      
      <div className="relative p-6">
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ 
              backgroundColor: 'rgba(139, 92, 246, 0.2)',
              boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)'
            }}
          >
            <ClipboardCheck className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Executive Review Backlog</h3>
            <p className="text-sm text-slate-400">Items awaiting executive decision</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <p 
              className="text-3xl font-black"
              style={{ 
                color: metrics?.pending_reviews > 0 ? '#F59E0B' : '#10B981',
                textShadow: metrics?.pending_reviews > 0 ? '0 0 20px rgba(245, 158, 11, 0.5)' : '0 0 20px rgba(16, 185, 129, 0.5)'
              }}
            >
              {metrics?.pending_reviews || 0}
            </p>
            <p className="text-xs text-slate-400 mt-1 font-medium uppercase tracking-wider">Pending Reviews</p>
          </div>
          
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <p 
              className="text-3xl font-black"
              style={{ 
                color: (metrics?.oldest_pending_hours || 0) > 48 ? '#EF4444' : 'white',
                textShadow: (metrics?.oldest_pending_hours || 0) > 48 ? '0 0 20px rgba(239, 68, 68, 0.5)' : 'none'
              }}
            >
              {(metrics?.oldest_pending_hours || 0).toFixed(0)}h
            </p>
            <p className="text-xs text-slate-400 mt-1 font-medium uppercase tracking-wider">Oldest Pending</p>
          </div>
          
          <div className={`p-4 rounded-xl relative overflow-hidden ${slaAtRisk ? 'animate-pulse' : ''}`} style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            {slaAtRisk && (
              <div 
                className="absolute inset-0 opacity-20"
                style={{ backgroundColor: '#EF4444' }}
              />
            )}
            <p 
              className="text-3xl font-black relative"
              style={{ 
                color: slaAtRisk ? '#EF4444' : '#10B981',
                textShadow: slaAtRisk ? '0 0 20px rgba(239, 68, 68, 0.5)' : '0 0 20px rgba(16, 185, 129, 0.5)'
              }}
            >
              {metrics?.sla_at_risk || 0}
            </p>
            <p className="text-xs text-slate-400 mt-1 font-medium uppercase tracking-wider relative">SLA at Risk</p>
          </div>
          
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <p className="text-3xl font-black" style={{ color: 'white' }}>
              {(metrics?.avg_decision_time_hours || 0).toFixed(1)}h
            </p>
            <p className="text-xs text-slate-400 mt-1 font-medium uppercase tracking-wider">Avg Decision Time</p>
          </div>
        </div>
        
        {slaAtRisk && (
          <div 
            className="mt-4 p-4 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }}
          >
            <div className="relative">
              <div className="absolute inset-0 animate-ping rounded-full opacity-30" style={{ backgroundColor: '#EF4444' }} />
              <AlertTriangle className="w-5 h-5 relative" style={{ color: '#EF4444' }} />
            </div>
            <p className="text-sm text-red-300">
              <span className="font-bold">{metrics?.sla_at_risk} project(s)</span> approaching or past SLA threshold — immediate action required
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================
// PREMIUM PROJECT CARD
// ================================================
const PremiumProjectCard = ({ project, onClick }) => {
  const isOnTrack = project.status === 'on-track';
  const statusConfig = isOnTrack 
    ? { color: '#00C9A7', bg: 'rgba(0, 201, 167, 0.1)', label: 'On Track', icon: TrendingUp }
    : { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', label: 'Focus Required', icon: AlertTriangle, glow: true };
  
  return (
    <div 
      onClick={onClick}
      className="relative p-5 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group overflow-hidden"
      style={{
        backgroundColor: DESIGN.colors.card,
        borderLeft: `4px solid ${statusConfig.color}`,
        boxShadow: statusConfig.glow ? `0 0 20px ${statusConfig.color}30` : DESIGN.shadows.md
      }}
    >
      {/* Subtle hover gradient */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `linear-gradient(135deg, ${statusConfig.bg} 0%, transparent 50%)`
        }}
      />
      
      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h4 
              className="text-base font-bold truncate group-hover:text-clip"
              style={{ color: DESIGN.colors.textPrimary }}
            >
              {project.name}
            </h4>
            <p 
              className="text-xs mt-1 line-clamp-1"
              style={{ color: DESIGN.colors.textMuted }}
            >
              {project.description}
            </p>
          </div>
          <div 
            className="flex items-center gap-2 px-3 py-1.5 rounded-full ml-3 flex-shrink-0"
            style={{ 
              backgroundColor: statusConfig.bg,
              boxShadow: statusConfig.glow ? `0 0 15px ${statusConfig.color}40` : 'none'
            }}
          >
            <statusConfig.icon className="w-3.5 h-3.5" style={{ color: statusConfig.color }} />
            <span 
              className="text-xs font-bold"
              style={{ color: statusConfig.color }}
            >
              {statusConfig.label}
            </span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>Progress</span>
            <span 
              className="text-sm font-bold"
              style={{ color: statusConfig.color }}
            >
              {project.completion_percentage}%
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${project.completion_percentage}%`,
                background: `linear-gradient(90deg, ${statusConfig.color}CC 0%, ${statusConfig.color} 100%)`,
                boxShadow: `0 0 10px ${statusConfig.color}60`
              }}
            />
          </div>
        </div>
        
        <div 
          className="flex items-center justify-between text-xs pt-3 border-t"
          style={{ borderColor: DESIGN.colors.borderSubtle }}
        >
          <span style={{ color: DESIGN.colors.textMuted }}>
            <span className="font-semibold" style={{ color: DESIGN.colors.textSecondary }}>{project.active_tasks}</span> active tasks
          </span>
          <span 
            className="flex items-center gap-1 font-medium opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ color: statusConfig.color }}
          >
            View details <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </div>
  );
};

// ================================================
// MAIN PULSE PAGE
// ================================================
const Pulse = () => {
  const navigate = useNavigate();
  const [orgMetrics, setOrgMetrics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewPulseMetrics, setReviewPulseMetrics] = useState(null);
  
  // Demo role switcher state - initialize with CEO data
  const [activeRole, setActiveRole] = useState('ceo');
  const [demoData, setDemoData] = useState(() => getPulseRoleData('ceo'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, projectsRes, reviewRes] = await Promise.all([
          metricsAPI.getPulse(),
          projectAPI.getAll(),
          reviewAPI.getPulseMetrics()
        ]);
        setOrgMetrics(metricsRes.data);
        setProjects(projectsRes.data.projects || []);
        setReviewPulseMetrics(reviewRes.data);
      } catch (error) {
        console.error('Error fetching pulse data, using demo data:', error);
        // Use demo data as fallback - already set in state
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Role change handler
  const handleRoleChange = useCallback((newRole) => {
    setActiveRole(newRole);
    const roleData = getPulseRoleData(newRole);
    setDemoData(roleData);
  }, []);

  // Get display metrics - use demo data if available, otherwise API data
  const displayMetrics = demoData || orgMetrics;

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: `${DESIGN.colors.primary}30`, borderTopColor: DESIGN.colors.primary }} />
          </div>
          <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>Loading pulse data...</p>
        </div>
      </div>
    );
  }

  if (!displayMetrics) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-3" style={{ color: DESIGN.colors.error }} />
          <p className="font-semibold" style={{ color: DESIGN.colors.error }}>Error loading data</p>
        </div>
      </div>
    );
  }

  const getMomentumLabel = (momentum) => {
    const labels = {
      'Strong': 'Excellent Performance',
      'Building': 'Tracking Progress',
      'Steady': 'Stable Execution',
      'Focus Required': 'Action Needed'
    };
    return labels[momentum] || 'Tracking';
  };

  const getMomentumMessage = (momentum, scopeLabel) => {
    const scope = scopeLabel || 'Your organization';
    const messages = {
      'Strong': `${scope} is executing with exceptional consistency. Teams are aligned, deliverables are on track, and momentum is accelerating.`,
      'Building': `Momentum is building across ${scope.toLowerCase()}. Maintain current focus and watch for opportunities to accelerate further.`,
      'Steady': `Execution is stable and consistent across ${scope.toLowerCase()}. Monitor emerging patterns and proactively address any friction points.`,
      'Focus Required': `Attention is needed to restore momentum in ${scope.toLowerCase()}. Review overdue items and address blockers immediately.`
    };
    return messages[momentum] || `Monitoring execution momentum across ${scope.toLowerCase()}.`;
  };

  const velocityChange = displayMetrics?.velocity_this_week && displayMetrics?.velocity_last_week 
    ? displayMetrics.velocity_this_week - displayMetrics.velocity_last_week 
    : 0;
  const isUniqueIP = demoData?.isUniqueIP;
  const isChiefOfStaff = demoData?.isChiefOfStaff;
  const isPersonalView = demoData?.isPersonalView;
  const accentColor = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primary 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primary 
      : DESIGN.colors.primary;
  const accentDark = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primaryDark 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primaryDark 
      : DESIGN.colors.primaryDark;
  const accentSubtle = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primarySubtle 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primarySubtle 
      : DESIGN.colors.primarySubtle;
  const accentGlow = isChiefOfStaff 
    ? `0 0 20px ${CHIEF_OF_STAFF_COLORS.primaryGlow}` 
    : isUniqueIP 
      ? `0 0 20px ${UNIQUE_IP_COLORS.primaryGlow}` 
      : DESIGN.shadows.glowPrimary;

  // CHIEF OF STAFF VIEW - Premium War Room Command Center
  if (isChiefOfStaff && demoData?.executiveMomentum) {
    // Generate sparkline data for KPIs
    const kpiSparklines = {
      followUps: [8, 10, 9, 11, 10, 12, 11, 12],
      decisions: [12, 14, 16, 15, 17, 16, 18, 18],
      escalations: [7, 6, 8, 5, 6, 5, 4, 4],
      timeProtected: [72, 74, 73, 76, 75, 77, 78, 78]
    };

    return (
      <div className="space-y-8" data-testid="pulse-page">
        <DemoRoleSwitcher activeRole={activeRole} onRoleChange={handleRoleChange} />
        
        {/* Premium Command Center Hero Banner */}
        <PulseCommandCenterHero 
          momentum={demoData.executiveMomentum}
          scope={demoData.scope_label}
        />

        {/* Premium Stat Cards with Sparklines */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <PulseStatCard 
            label="Items Requiring Follow-Up"
            value={12}
            trend="up"
            trendValue={3}
            trendDirection="up-is-bad"
            sparklineData={kpiSparklines.followUps}
            icon={AlertTriangle}
          />
          <PulseStatCard 
            label="Decisions Made This Week"
            value={18}
            trend="stable"
            trendValue={0}
            sparklineData={kpiSparklines.decisions}
            icon={CheckCircle2}
          />
          <PulseStatCard 
            label="Cross-Dept Escalations"
            value={4}
            trend="down"
            trendValue={2}
            trendDirection="down-is-good"
            sparklineData={kpiSparklines.escalations}
            icon={Users}
          />
          <PulseStatCard 
            label="Executive Time Protected"
            value={78}
            unit="%"
            trend="up"
            trendValue={4}
            sparklineData={kpiSparklines.timeProtected}
            icon={Shield}
          />
        </div>

        {/* Two Column Layout - Work Feed & Soul Food Health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PulseWorkAdvancingFeed items={demoData.workAdvancingFeed} />
          <SoulFoodDivisionHealth health={demoData.soulFoodDivisionHealth} />
        </div>

        {/* Cognitive Lanes Overview */}
        <CognitiveLanesOverview lanes={demoData.lane_performance} />

        {/* Executive Review Pipeline - Primary Responsibility */}
        <ExecutiveReviewPipeline items={demoData.executiveReviewBacklog} />
      </div>
    );
  }

  // PERSONAL VIEW for Team Member and Unique IP Operations
  if (isPersonalView && demoData?.personalMomentum) {
    return (
      <div className="space-y-8" data-testid="pulse-page">
        {/* Demo Role Switcher */}
        <DemoRoleSwitcher 
          activeRole={activeRole} 
          onRoleChange={handleRoleChange}
        />
        
        {/* Personal Momentum Hero */}
        <PersonalMomentumHero data={demoData.personalMomentum} isUniqueIP={isUniqueIP} />
        
        {/* Personal KPIs Grid */}
        {demoData.personalKPIs && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <PersonalKPICard 
              label={demoData.personalKPIs.tasksCompletedToday.label}
              value={demoData.personalKPIs.tasksCompletedToday.value}
              target={demoData.personalKPIs.tasksCompletedToday.target}
              progress={demoData.personalKPIs.tasksCompletedToday.progress}
              isUniqueIP={isUniqueIP}
            />
            {demoData.personalKPIs.onTimeRate ? (
              <PersonalKPICard 
                label={demoData.personalKPIs.onTimeRate.label}
                value={demoData.personalKPIs.onTimeRate.value}
                trend={demoData.personalKPIs.onTimeRate.trend}
                trendLabel={demoData.personalKPIs.onTimeRate.trendLabel}
                isUniqueIP={isUniqueIP}
              />
            ) : (
              <PersonalKPICard 
                label={demoData.personalKPIs.shiftsCompletedMonth.label}
                value={demoData.personalKPIs.shiftsCompletedMonth.value}
                sublabel={demoData.personalKPIs.shiftsCompletedMonth.sublabel}
                isUniqueIP={isUniqueIP}
              />
            )}
            {demoData.personalKPIs.currentLoad ? (
              <PersonalKPICard 
                label={demoData.personalKPIs.currentLoad.label}
                value={demoData.personalKPIs.currentLoad.value}
                status={demoData.personalKPIs.currentLoad.status}
                isUniqueIP={isUniqueIP}
              />
            ) : (
              <PersonalKPICard 
                label={demoData.personalKPIs.onTimeOpsRate.label}
                value={demoData.personalKPIs.onTimeOpsRate.value}
                trend={demoData.personalKPIs.onTimeOpsRate.trend}
                trendLabel={demoData.personalKPIs.onTimeOpsRate.trendLabel}
                isUniqueIP={isUniqueIP}
              />
            )}
            {demoData.personalKPIs.streak ? (
              <PersonalKPICard 
                label={demoData.personalKPIs.streak.label}
                value={demoData.personalKPIs.streak.value}
                sublabel={demoData.personalKPIs.streak.sublabel}
                isUniqueIP={isUniqueIP}
              />
            ) : (
              <PersonalKPICard 
                label={demoData.personalKPIs.issuesReported.label}
                value={demoData.personalKPIs.issuesReported.value}
                sublabel={demoData.personalKPIs.issuesReported.sublabel}
                isUniqueIP={isUniqueIP}
              />
            )}
          </div>
        )}

        {/* Personal Work Feed & My Lane/Operations Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {demoData.personalWorkFeed && (
            <PersonalWorkFeed items={demoData.personalWorkFeed} isUniqueIP={isUniqueIP} />
          )}
          {demoData.myLanePerformance && (
            <MyLanePerformance data={demoData.myLanePerformance} isUniqueIP={isUniqueIP} />
          )}
          {demoData.myOperationsPerformance && (
            <div 
              className="rounded-2xl p-6"
              style={{ 
                backgroundColor: DESIGN.colors.card,
                border: `1px solid ${DESIGN.colors.border}`
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${accentColor}15` }}
                >
                  <Target className="w-5 h-5" style={{ color: accentColor }} />
                </div>
                <div>
                  <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                    My Operations Performance
                  </h3>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>This week vs last week</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-2xl font-bold" style={{ color: accentColor }}>
                    {demoData.myOperationsPerformance.thisWeek.tasksCompleted}
                  </p>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Tasks</p>
                  <p className="text-xs mt-1" style={{ color: '#10B981' }}>
                    +{demoData.myOperationsPerformance.thisWeek.tasksCompleted - demoData.myOperationsPerformance.lastWeek.tasksCompleted}
                  </p>
                </div>
                <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-2xl font-bold" style={{ color: accentColor }}>
                    {demoData.myOperationsPerformance.thisWeek.eventsSupported}
                  </p>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Events</p>
                </div>
                <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-2xl font-bold" style={{ color: accentColor }}>
                    {demoData.myOperationsPerformance.reliabilityScore}%
                  </p>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Reliability</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Today's Priorities & Personal Wins / Shift Log */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {demoData.todaysPriorities && (
            <TodaysPriorities items={demoData.todaysPriorities} isUniqueIP={isUniqueIP} />
          )}
          {demoData.personalWins && (
            <PersonalWins items={demoData.personalWins} isUniqueIP={isUniqueIP} />
          )}
          {demoData.shiftLog && (
            <PersonalWins items={demoData.shiftLog} title="Shift Log" isShiftLog isUniqueIP={isUniqueIP} />
          )}
        </div>

        {/* My Upcoming Deadlines (Team Member) or Upcoming Events (Ops) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {demoData.myUpcomingDeadlines && (
            <MyUpcomingDeadlines items={demoData.myUpcomingDeadlines} isUniqueIP={isUniqueIP} />
          )}
          {demoData.upcomingEvents && (
            <UpcomingEvents events={demoData.upcomingEvents} isUniqueIP={isUniqueIP} />
          )}
        </div>

        {/* CSS for animations */}
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(200%); }
          }
        `}</style>
      </div>
    );
  }

  // STANDARD VIEW (All other roles)
  return (
    <div className="space-y-8" data-testid="pulse-page">
      {/* Demo Role Switcher */}
      <DemoRoleSwitcher 
        activeRole={activeRole} 
        onRoleChange={handleRoleChange}
      />
      
      {/* Hero: Execution Momentum */}
      <div 
        className="relative rounded-3xl overflow-hidden"
        style={{
          background: isUniqueIP 
            ? 'linear-gradient(135deg, #451a03 0%, #78350f 40%, #451a03 100%)'
            : 'linear-gradient(135deg, #0D1B2A 0%, #1B263B 40%, #0D1B2A 100%)'
        }}
      >
        {/* Animated background elements */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: isUniqueIP
              ? `radial-gradient(ellipse at 20% 50%, ${UNIQUE_IP_COLORS.primary}40 0%, transparent 50%),
                 radial-gradient(ellipse at 80% 80%, #EAB30840 0%, transparent 40%)`
              : `radial-gradient(ellipse at 20% 50%, ${DESIGN.colors.primary}40 0%, transparent 50%),
                 radial-gradient(ellipse at 80% 80%, #3B82F640 0%, transparent 40%)`,
          }}
        />
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.03"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        
        <div className="relative p-8 md:p-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Execution Momentum
                </p>
                {demoData?.scope_label && (
                  <span 
                    className="text-xs font-bold px-3 py-1 rounded-full"
                    style={{ 
                      backgroundColor: isUniqueIP ? UNIQUE_IP_COLORS.primarySubtle : 'rgba(0, 201, 167, 0.15)',
                      color: isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary
                    }}
                  >
                    {demoData.scope_label}
                  </span>
                )}
              </div>
              <MomentumIndicator 
                status={displayMetrics.execution_momentum} 
                label={getMomentumLabel(displayMetrics.execution_momentum)}
              />
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Live • Updated just now
            </div>
          </div>
          
          <p className="text-base text-slate-300 max-w-3xl leading-relaxed mb-8">
            {getMomentumMessage(displayMetrics.execution_momentum, demoData?.scope_label)}
          </p>
          
          {/* Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <PremiumStatCard 
              label="Velocity This Week"
              value={displayMetrics.velocity_this_week}
              change={velocityChange}
              changeLabel="vs last week"
              status={velocityChange > 0 ? 'positive' : velocityChange < 0 ? 'negative' : 'neutral'}
              icon={Zap}
            />
            <PremiumStatCard 
              label="On-Time Phase Rate"
              value={`${Math.round(displayMetrics.on_time_phase_rate * 100)}%`}
              status={displayMetrics.on_time_phase_rate >= 0.8 ? 'positive' : displayMetrics.on_time_phase_rate >= 0.6 ? 'warning' : 'negative'}
              icon={Clock}
            />
            <PremiumStatCard 
              label="Active Momentum"
              value={displayMetrics.active_momentum_count}
              status="positive"
              icon={TrendingUp}
            />
            <PremiumStatCard 
              label="Needing Attention"
              value={displayMetrics.needing_attention_count}
              status={displayMetrics.needing_attention_count > 0 ? 'negative' : 'positive'}
              icon={AlertTriangle}
            />
          </div>
        </div>
      </div>

      {/* Work Advancing & Lane Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Work Advancing - Live Activity Feed */}
        <div 
          className="rounded-2xl p-6"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            boxShadow: DESIGN.shadows.lg
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: isUniqueIP ? UNIQUE_IP_COLORS.primarySubtle : DESIGN.colors.primarySubtle }}
              >
                <Activity className="w-5 h-5" style={{ color: isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary }} />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                  Work Advancing
                </h3>
                <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Real-time activity feed</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs" style={{ color: DESIGN.colors.textMuted }}>
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Live
            </div>
          </div>
          
          <div className="space-y-3">
            <ActivityFeedItem 
              title="Build component library foundation"
              status="Phase completed"
              timestamp="2 hours ago"
              type="completed"
            />
            <ActivityFeedItem 
              title="API integration sprint"
              status="In progress"
              timestamp="Active now"
              type="in-progress"
            />
            <ActivityFeedItem 
              title="Conduct user interviews"
              status="Step completed"
              timestamp="4 hours ago"
              type="completed"
            />
            <ActivityFeedItem 
              title="Enterprise marketing collateral"
              status="Needs review"
              timestamp="1 day ago"
              type="attention"
            />
          </div>
        </div>

        {/* Lane Performance */}
        <div 
          className="rounded-2xl p-6"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            boxShadow: DESIGN.shadows.lg
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: DESIGN.colors.blueSubtle }}
            >
              <Target className="w-5 h-5" style={{ color: DESIGN.colors.blue }} />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                Lane Performance
              </h3>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>On-time delivery by cognitive lane</p>
            </div>
          </div>
          
          <div className="space-y-5">
            {(displayMetrics.lane_performance || []).map((lane) => {
              // Check if lane has direct lane_name (demo data) or needs lookup
              const laneData = lane.lane_name 
                ? lane 
                : COGNITIVE_LANES.find(l => l.id === lane.lane);
              const laneName = lane.lane_name || laneData?.name || lane.lane;
              const laneColor = lane.color || laneData?.color || DESIGN.colors.primary;
              return (
                <LanePerformanceBar 
                  key={lane.lane || laneName}
                  lane={laneName}
                  percentage={Math.round(lane.on_time_percentage * 100)}
                  activeCount={lane.active_count}
                  color={laneColor}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Executive Review Backlog - hide for team members and operations */}
      {reviewPulseMetrics && !demoData?.hideExecutiveMetrics && (
        <ExecutiveReviewCard metrics={demoData?.review_backlog || reviewPulseMetrics} />
      )}

      {/* Active Projects */}
      <div 
        className="rounded-2xl p-6"
        style={{ 
          backgroundColor: DESIGN.colors.card,
          border: `1px solid ${DESIGN.colors.border}`,
          boxShadow: DESIGN.shadows.lg
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
            >
              <Target className="w-5 h-5" style={{ color: '#8B5CF6' }} />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                Active Projects
              </h3>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                {projects.length} projects in progress
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/projects')} 
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:shadow-md"
            style={{ 
              backgroundColor: isUniqueIP ? UNIQUE_IP_COLORS.primarySubtle : DESIGN.colors.primarySubtle,
              color: isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary
            }}
          >
            View all
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.slice(0, 4).map((project) => (
            <PremiumProjectCard 
              key={project.id}
              project={project}
              onClick={() => navigate(`/projects/${project.id}`)}
            />
          ))}
        </div>
      </div>

      {/* Level Focus - hide for operations and team members */}
      {!demoData?.hideExecutiveMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div 
            className="rounded-2xl p-6 relative overflow-hidden"
            style={{ 
              background: isUniqueIP
                ? `linear-gradient(135deg, ${UNIQUE_IP_COLORS.primarySubtle} 0%, ${DESIGN.colors.card} 100%)`
                : `linear-gradient(135deg, ${DESIGN.colors.primarySubtle} 0%, ${DESIGN.colors.card} 100%)`,
              border: `1px solid ${DESIGN.colors.border}`
            }}
          >
            <div 
              className="absolute top-0 right-0 w-32 h-32 opacity-20"
              style={{
                background: `radial-gradient(circle, ${isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary} 0%, transparent 70%)`
              }}
            />
            <p 
              className="text-xs font-bold uppercase tracking-wider mb-2 relative"
              style={{ color: DESIGN.colors.textMuted }}
            >
              L4 Decisions (Strategic)
            </p>
            <p 
              className="text-5xl font-black relative"
              style={{ 
                color: isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary,
                textShadow: isUniqueIP ? `0 0 30px ${UNIQUE_IP_COLORS.primaryGlow}` : `0 0 30px ${DESIGN.colors.primaryGlow}`
              }}
            >
              {displayMetrics.level_focus?.l4_decisions || 0}
            </p>
            <p className="text-sm mt-2 relative" style={{ color: DESIGN.colors.textMuted }}>
              Executive decisions this week
            </p>
          </div>
          
          <div 
            className="rounded-2xl p-6 relative overflow-hidden"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.blueSubtle} 0%, ${DESIGN.colors.card} 100%)`,
              border: `1px solid ${DESIGN.colors.border}`
            }}
          >
            <div 
              className="absolute top-0 right-0 w-32 h-32 opacity-20"
              style={{
                background: `radial-gradient(circle, ${DESIGN.colors.blue} 0%, transparent 70%)`
              }}
            />
            <p 
              className="text-xs font-bold uppercase tracking-wider mb-2 relative"
              style={{ color: DESIGN.colors.textMuted }}
            >
              Operational Load
            </p>
            <p 
              className="text-5xl font-black relative"
              style={{ 
                color: DESIGN.colors.blue,
                textShadow: `0 0 30px ${DESIGN.colors.blueGlow}`
              }}
            >
              {displayMetrics.level_focus?.operational_load_percentage || 0}%
            </p>
            <p className="text-sm mt-2 relative" style={{ color: DESIGN.colors.textMuted }}>
              L1-L2 execution work capacity
            </p>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
      `}</style>
    </div>
  );
};

export default Pulse;
